import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton, Tooltip,
} from '@mui/material';
import {
    Login as LoginIcon,
    FilterAlt as FilterAltIcon,
    Place as PlaceIcon,
    ArrowBackIos,
    ArrowForwardIos,
} from '@mui/icons-material';

import MapCedro from '../components/MapCedro';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import api from '../services/api';
import ReportMilkProducerDialog from './ReportMilkProducerDialog';

const theme = createTheme({
    palette: {
        primary: {
            main: '#10641c',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#e6d700',
            contrastText: '#000000',
        },
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    },
});

const itemsPerPage = 5;
const chipsPerPage = 5;

const Dashboard = () => {
    const [businessTypes, setBusinessTypes] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [filter, setFilter] = useState('todos');
    const [page, setPage] = useState(1);
    const [chipPage, setChipPage] = useState(1);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('type-business/users');
                const data = response.data;

                const filteredBusinessTypes = data
                    .filter(type => type.users && type.users.length > 0)
                    .map(type => type.name)
                    .sort((a, b) => a.localeCompare(b));

                setBusinessTypes(filteredBusinessTypes);

                const businessMap = new Map();
                data.forEach(type => {
                    (type.users || []).forEach(user => {
                        if (!businessMap.has(user.id)) {
                            businessMap.set(user.id, {
                                id: user.id,
                                name: user.name,
                                size: user.businessSize?.name ?? '',
                                //description: user.observation || 'Sem descrição',
                                position: user.location?.coordinates
                                    ? [user.location.coordinates[1], user.location.coordinates[0]]
                                    : [],
                                neighborhood: user.neighborhood?.name ?? '',
                                //number: user.number,
                                types: [],
                                // phone: user.phone,
                                visible: user.visible,
                            });
                        }
                        businessMap.get(user.id).types.push(type.name);
                    });
                });

                const mockBusinesses = Array.from(businessMap.values()).filter(
                    business => business.position.length === 2
                );

                setBusinesses(mockBusinesses);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };

        fetchData();
    }, []);

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1);
    };

    const handleChipPageChange = (direction) => {
        setChipPage(prev => {
            const newPage = direction === 'next' ? prev + 1 : prev - 1;
            return Math.max(1, Math.min(newPage, Math.ceil(businessTypes.length / chipsPerPage)));
        });
    };

    const filteredBusinesses = filter === 'todos'
        ? businesses
        : businesses.filter(business => business.types.includes(filter));

    const totalPages = Math.ceil(filteredBusinesses.length / itemsPerPage);
    const currentBusinesses = filteredBusinesses.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const totalChipPages = Math.ceil(businessTypes.length / chipsPerPage);
    const startIndex = (chipPage - 1) * chipsPerPage;
    const currentChips = businessTypes.slice(startIndex, startIndex + chipsPerPage);

    const [selectedType, setSelectedType] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleOpenDialog = (type) => {
        setSelectedType(type);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedType(null);
    };

    const typeCounts = {};

    filteredBusinesses.forEach(business => {
        business.types.forEach(type => {
            if (!typeCounts[type]) {
                typeCounts[type] = 0;
            }
            typeCounts[type]++;
        });
    });

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#f8f9fa',
                }}
            >
                <Box
                    sx={{
                        background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                        color: 'white',
                        p: 3,
                        boxShadow: 3,
                    }}
                >
                    <Container maxWidth="xl" >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center', // Alinhamento vertical
                            justifyContent: 'space-between', // Espaço entre os elementos
                            flexWrap: 'nowrap', // Impede quebra de linha
                            gap: 2,
                        }}>
                            <Box>
                                <Typography variant="h4" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '32px',
                                    mb: 1,
                                    fontFamily: '"Poppins", sans-serif',
                                }}>
                                    Conecta Mais
                                </Typography>
                                <Typography variant="subtitle1" sx={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '1rem',
                                }}>
                                    Mapeamento de empreendimentos locais
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<LoginIcon />}
                                href="/login"
                                sx={{
                                    bgcolor: 'white',
                                    color: '#10641c',
                                    px: 3,
                                    py: 1,
                                    borderRadius: '50px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        bgcolor: '#f0f0f0',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
                                    },
                                    transition: 'all 0.3s ease',
                                    minWidth: 'auto',
                                }}
                            >
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                    Acesso restrito
                                </Box>
                            </Button>
                        </Box>
                    </Container>
                </Box>

                <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <Container maxWidth="xl" sx={{
                        display: 'flex',
                        flexDirection: 'row', // Sempre em linha, independente do tamanho da tela
                        alignItems: 'center',
                        gap: 1,
                        position: 'relative',
                        overflow: 'hidden', // Para evitar que elementos vazem
                        flexWrap: 'wrap' // Permite quebra de linha se necessário
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterAltIcon sx={{ color: '#10641c', mr: 0 }} />
                            <Typography variant="subtitle2" sx={{
                                fontWeight: 500,
                                color: '#555',
                                mr: 1,
                                display: { xs: 'none', sm: 'block' }
                            }}>
                                Filtrar por:
                            </Typography>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0 // Importante para overflow
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flex: 1,
                                gap: 1,
                                overflow: 'auto', // Permite scroll horizontal
                                scrollbarWidth: 'none', // Esconde a barra de scroll no Firefox
                                '&::-webkit-scrollbar': { // Esconde a barra de scroll no Chrome/Safari
                                    display: 'none'
                                },
                                py: 1 // Espaço vertical para os chips
                            }}>
                                <Chip
                                    label="Todos"
                                    onClick={() => handleFilterChange('todos')}
                                    variant={filter === 'todos' ? 'contained' : 'outlined'}
                                    sx={{ minWidth: 'fit-content' }}
                                    color={filter === 'todos' ? 'primary' : undefined}
                                />
                                {currentChips.map((typeName) => (
                                    <Chip
                                        key={typeName}
                                        label={typeName}
                                        onClick={() => handleFilterChange(typeName)}
                                        variant={filter === typeName ? 'contained' : 'outlined'}
                                        sx={{ minWidth: 'fit-content' }}
                                        color={filter === typeName ? 'primary' : undefined}
                                    />
                                ))}
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                ml: 1,
                                flexShrink: 0, // Impede que diminua
                                gap: 0.25
                            }}>
                                <IconButton
                                    onClick={() => handleChipPageChange('prev')}
                                    disabled={chipPage === 1}
                                    sx={{ color: '#10641c', padding: '2px', minWidth: 0, '& svg': { fontSize: '0.90rem' } }}
                                    size="small"
                                >
                                    <ArrowBackIos fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" sx={{ mx: 0.25, color: '#10641c', fontSize: '0.90rem' }}>
                                    {chipPage}/{totalChipPages}
                                </Typography>
                                <IconButton
                                    onClick={() => handleChipPageChange('next')}
                                    disabled={chipPage === totalChipPages}
                                    sx={{ color: '#10641c', padding: '2px', minWidth: 0, '& svg': { fontSize: '0.90rem' } }}
                                    size="small"
                                >
                                    <ArrowForwardIos fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Container>
                </Box>
                <Container sx={{ mt: 4 }} maxWidth="xl">
                    <Grid container spacing={2} sx={{ flexWrap: 'nowrap', overflowX: 'auto', pb: 2 }}>
                        {Object.entries(typeCounts).map(([type, count]) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={type} sx={{ minWidth: 275, marginRight: { xs: "80px", sm: 10 } }}>
                                <Card sx={{
                                    backgroundColor: '#f1f8f4',
                                    borderLeft: '6px solid #10641c',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                    width: 300,            // Define uma largura fixa
                                    height: 110,           // Define uma altura fixa
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    p: 2,
                                }}>
                                    <CardContent>
                                        <Tooltip title={type}>
                                            <Typography variant="h6" sx={{
                                                color: '#10641c',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '100%'
                                            }}>
                                                {count} {count === 1 ? type : `${type}`}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Empreendimentos cadastrados
                                            </Typography>
                                        </Tooltip>
                                    </CardContent>
                                    {type === 'Produtores de Leite' && (
                                        <Button
                                            onClick={() => handleOpenDialog(type)}
                                            size="small"
                                            sx={{ mt: 1, textTransform: 'none', color: '#10641c', fontWeight: 'bold' }}
                                        >
                                            Mais informações
                                        </Button>
                                    )}

                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                <ReportMilkProducerDialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    businesses={filteredBusinesses}
                />

                <Container sx={{ mt: 4, marginBottom: '3px' }} maxWidth="xl">
                    <Grid item xs={12}>
                        <Card sx={{
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                        }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                                }}>
                                    <PlaceIcon sx={{
                                        color: '#10641c',
                                        mr: 1,
                                        fontSize: '1.5rem',
                                    }} />
                                    <Typography variant="h6" sx={{
                                        color: '#10641c',
                                        fontWeight: '600',
                                        fontSize: '19px'
                                    }}>
                                        Empreendimentos em Cedro
                                    </Typography>
                                </Box>
                                <Box >
                                    <MapCedro businesses={filteredBusinesses} filter={filter} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Container>

                <Box sx={{ bgcolor: '#10641c', color: 'white', p: 3, textAlign: 'center', mt: 'auto' }}>
                    <Container maxWidth="xl">
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255, 255, 2500, 0.8)',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                        >
                            © {new Date().getFullYear()} Conecta Mais - Todos os direitos reservados
                        </Typography>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Dashboard;